import { memo, useState, useCallback, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { InputAdornment, styled } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useDebounce } from 'utils/hooks';
import { TextField, type Props as TextFieldProps } from 'components/mui/common/TextField';

const Wrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '8px',
      borderColor: theme.palette.grey[300],
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 10px',
    fontSize: '13px',
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    fontSize: '18px',
    color: theme.palette.text.secondary,
  },
}));

export const SearchField = memo(
  ({
    value: valueProp,
    onChange,
    maxWidth,
    autoFocus = false,
    dataTestId,
    ...rest
  }: TextFieldProps & { value?: string; maxWidth?: string; autoFocus?: boolean; dataTestId?: string }) => {
    const [value, setValue] = useState(valueProp ?? '');
    const debouncedValue = useDebounce<string>(value, 500);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    }, []);

    useEffect(() => {
      onChange?.({ target: { value: debouncedValue } } as ChangeEvent<HTMLInputElement>);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    return (
      <Wrapper>
        <TextField
          dataTestId={dataTestId ?? 'search-field'}
          autoFocus={autoFocus}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          value={value}
          onChange={handleChange}
          {...rest}
          fullWidth
          disableHelperText
        />
      </Wrapper>
    );
  }
);
